import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { api } from '../lib/api.js';
import type { CompanionNotice } from '../lib/companion.js';
import { MeetingRecorder } from '../components/MeetingRecorder.js';

const MeetingContext = createContext<{ open: (projectId: string) => void; notice: CompanionNotice | null; acknowledge: () => void; reveal: () => void; pending: number } | null>(null);

/** Lives above route content: changing conversations cannot unmount an active recorder. */
export function MeetingRecorderProvider({ children }: { children: ReactNode }) {
  const [notices, setNotices] = useState<CompanionNotice[]>([]);
  const [pending, setPending] = useState<string[]>([]);
  const onSaved = useCallback((id: string) => setPending(current => current.includes(id) ? current : [...current, id]), []);
  const setNotice = useCallback((notice: CompanionNotice | null) => {
    setNotices(current => {
      const rest = current.filter(n => !n.id.startsWith('recorder:'));
      return notice ? [notice, ...rest] : rest;
    });
  }, []);
  useEffect(() => {
    if (!pending.length) return;
    let stopped = false;
    let timer: ReturnType<typeof setTimeout>;
    const poll = async () => {
      for (const id of pending) {
        try {
          const { recording } = await api.recordings.get(id);
          if (stopped) return;
          if (recording.status === 'ready' || recording.status === 'failed') {
            const next: CompanionNotice = { id: `transcript:${id}`, tone: recording.status === 'ready' ? 'ready' : 'attention', label: recording.status === 'ready' ? 'Your transcript is ready' : 'Transcription needs attention', href: `/r/${id}` };
            setNotices(current => current.some(n => n.id === next.id) ? current : [...current, next]);
            setPending(current => current.filter(item => item !== id));
          }
        } catch { /* Transient failures cannot establish completion. Retry while this session is open. */ }
      }
      if (!stopped) timer = setTimeout(() => void poll(), 5000);
    };
    void poll();
    return () => { stopped = true; clearTimeout(timer); };
  }, [pending]);
  const [projectId, setProjectId] = useState<string | null>(null);
  const [minimized, setMinimized] = useState(false);
  return (
    <MeetingContext.Provider value={{ notice: notices[0] ?? null, acknowledge: () => setNotices(current => current.slice(1)), reveal: () => setMinimized(false), open: (id) => { setProjectId((current) => current ?? id); setMinimized(false); }, pending: pending.length }}>
      {children}
      {projectId ? <MeetingRecorder onSaved={onSaved} onNotice={setNotice} projectId={projectId} minimized={minimized} onMinimize={() => setMinimized(!minimized)} onClose={() => { setProjectId(null); setNotice(null); }} /> : null}
    </MeetingContext.Provider>
  );
}
export function useMeetingRecorder() {
  const ctx = useContext(MeetingContext);
  if (!ctx) throw new Error('useMeetingRecorder outside MeetingRecorderProvider');
  return ctx;
}
